import { useState, useEffect, useCallback } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  InputAdornment,
  Snackbar,
  Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { dailyLoginService } from '@/data/dailyLoginService';
import { voucherService } from '@/data/voucherService';
import {
  BranchDailyLoginType,
  CreateDailyLoginTypeDto,
  LEGAL_VAT_RATES,
  UpdateDailyLoginTypeDto
} from '@/types/DailyLoginType.interface';
import {
  CreateVoucherTemplateDto,
  VoucherTemplate
} from '@/types/Voucher.interface';
import NumericInput from '@/components/NumericInput';
import VoucherTemplateDialog from '@/content/Management/Vouchers/VoucherTemplateDialog';

interface DailyLoginTypesManagerProps {
  branchId: number;
  /** Kupon şablonu oluştururken ürün listesinin çekileceği şirket. */
  companyId?: number;
}

const DailyLoginTypesManager = ({
  branchId,
  companyId
}: DailyLoginTypesManagerProps) => {
  const { t } = useTranslation();

  // State
  const [dailyLoginTypes, setDailyLoginTypes] = useState<
    BranchDailyLoginType[]
  >([]);
  const [voucherTemplates, setVoucherTemplates] = useState<VoucherTemplate[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<BranchDailyLoginType | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Form state
  const [formData, setFormData] = useState<CreateDailyLoginTypeDto>({
    name: '',
    description: '',
    price: 0,
    voucherTemplateId: 0,
    isActive: true,
    sortOrder: 0,
    vatRate: null,
    productPortionLabel: null,
    productPortionAmount: null,
    productPortionVatRate: null
  });
  const [selectedVoucherTemplate, setSelectedVoucherTemplate] =
    useState<VoucherTemplate | null>(null);
  // Giriş tipi formunu terk etmeden kupon şablonu oluşturma. Yeni sekme /
  // route değişikliği YOK — yarım dolmuş giriş tipi formu kaybolmamalı.
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);

  // Fetch daily login types
  const fetchDailyLoginTypes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await dailyLoginService.getTypes(branchId, true);
      setDailyLoginTypes(data || []);
    } catch (error) {
      console.error('Error fetching daily login types:', error);
      setNotification({
        open: true,
        message: t('dailyLoginTypes.error.fetch'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [branchId, t]);

  // Fetch voucher templates for autocomplete
  const fetchVoucherTemplates = useCallback(async () => {
    try {
      const result = await voucherService.getAll({ branchId, getAll: true });
      const templates = result?.items || [];
      setVoucherTemplates(Array.isArray(templates) ? templates : []);
    } catch (error) {
      console.error('Error fetching voucher templates:', error);
    }
  }, [branchId]);

  useEffect(() => {
    fetchDailyLoginTypes();
    fetchVoucherTemplates();
  }, [fetchDailyLoginTypes, fetchVoucherTemplates]);

  /**
   * Giriş tipi formunun içinden kupon şablonu oluşturur; kaydedince listeyi
   * yeniler ve yeni şablonu otomatik seçer, böylece kullanıcı forma geri dönüp
   * kuponu elle aramak zorunda kalmaz.
   */
  const handleCreateVoucherTemplate = async (
    targetBranchId: number,
    dto: CreateVoucherTemplateDto
  ): Promise<VoucherTemplate | void> => {
    try {
      // Uç `{ status, message, template }` sarmalı dönüyor; şablonun kendisi
      // `template` alanında. Sarmalsız bir dönüşe karşı da dayanıklı olsun.
      const response = await voucherService.create(targetBranchId, dto);
      const created: VoucherTemplate | undefined =
        response?.template ?? response;
      await fetchVoucherTemplates();
      if (created?.id) {
        setSelectedVoucherTemplate(created);
        setFormData((prev) => ({ ...prev, voucherTemplateId: created.id }));
      }
      setNotification({
        open: true,
        message: t('dailyLoginTypes.voucherTemplateCreated'),
        severity: 'success'
      });
      setVoucherDialogOpen(false);
      return created;
    } catch (error) {
      console.error('Error creating voucher template:', error);
      setNotification({
        open: true,
        message: t('dailyLoginTypes.error.voucherTemplateCreate'),
        severity: 'error'
      });
      throw error;
    }
  };

  // Handle dialog open for create
  const handleCreateClick = () => {
    setSelectedType(null);
    setFormData({
      name: '',
      description: '',
      price: 0,
      voucherTemplateId: 0,
      isActive: true,
      sortOrder: dailyLoginTypes.length,
      vatRate: null,
      productPortionLabel: null,
      productPortionAmount: null,
      productPortionVatRate: null
    });
    setSelectedVoucherTemplate(null);
    setDialogOpen(true);
  };

  // Handle dialog open for edit
  const handleEditClick = (type: BranchDailyLoginType) => {
    setSelectedType(type);
    setFormData({
      name: type.name,
      description: type.description || '',
      price: type.price,
      voucherTemplateId: type.voucherTemplateId,
      isActive: type.isActive,
      sortOrder: type.sortOrder,
      vatRate: type.vatRate ?? null,
      productPortionLabel: type.productPortionLabel ?? null,
      productPortionAmount: type.productPortionAmount ?? null,
      productPortionVatRate: type.productPortionVatRate ?? null
    });
    const template = voucherTemplates.find(
      (t) => t.id === type.voucherTemplateId
    );
    setSelectedVoucherTemplate(template || null);
    setDialogOpen(true);
  };

  // Handle delete click
  const handleDeleteClick = (type: BranchDailyLoginType) => {
    setSelectedType(type);
    setDeleteDialogOpen(true);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedType(null);
    setFormData({
      name: '',
      description: '',
      price: 0,
      voucherTemplateId: 0,
      isActive: true,
      sortOrder: 0
    });
    setSelectedVoucherTemplate(null);
  };

  // Handle save
  const handleSave = async () => {
    // Validation
    if (!formData.name.trim()) {
      setNotification({
        open: true,
        message: t('dailyLoginTypes.error.nameRequired'),
        severity: 'error'
      });
      return;
    }

    if (!formData.voucherTemplateId) {
      setNotification({
        open: true,
        message: t('dailyLoginTypes.error.voucherRequired'),
        severity: 'error'
      });
      return;
    }

    if (formData.price < 0) {
      setNotification({
        open: true,
        message: t('dailyLoginTypes.error.invalidPrice'),
        severity: 'error'
      });
      return;
    }

    // Fiş satır dekompozisyonu — client-side validation (backend de
    // reddediyor ama daha temiz hata gösterelim).
    const anyPortion =
      (formData.productPortionLabel != null &&
        formData.productPortionLabel !== '') ||
      formData.productPortionAmount != null ||
      formData.productPortionVatRate != null;
    const allPortion =
      formData.productPortionLabel != null &&
      formData.productPortionLabel.trim() !== '' &&
      formData.productPortionAmount != null &&
      formData.productPortionVatRate != null;
    if (anyPortion && !allPortion) {
      setNotification({
        open: true,
        message: t('dailyLoginTypes.error.partialDecomposition'),
        severity: 'error'
      });
      return;
    }
    if (
      allPortion &&
      (formData.productPortionAmount! <= 0 ||
        formData.productPortionAmount! >= formData.price)
    ) {
      setNotification({
        open: true,
        message: t('dailyLoginTypes.error.productAmountRange'),
        severity: 'error'
      });
      return;
    }

    try {
      setSaving(true);

      if (selectedType) {
        // Update
        const updateData: UpdateDailyLoginTypeDto = {
          name: formData.name,
          description: formData.description,
          price: formData.price,
          voucherTemplateId: formData.voucherTemplateId,
          isActive: formData.isActive,
          sortOrder: formData.sortOrder,
          vatRate: formData.vatRate ?? null,
          productPortionLabel: formData.productPortionLabel ?? null,
          productPortionAmount: formData.productPortionAmount ?? null,
          productPortionVatRate: formData.productPortionVatRate ?? null
        };
        await dailyLoginService.updateType(
          branchId,
          selectedType.id,
          updateData
        );
        setNotification({
          open: true,
          message: t('dailyLoginTypes.success.updated'),
          severity: 'success'
        });
      } else {
        // Create
        await dailyLoginService.createType(branchId, formData);
        setNotification({
          open: true,
          message: t('dailyLoginTypes.success.created'),
          severity: 'success'
        });
      }

      handleDialogClose();
      fetchDailyLoginTypes();
    } catch (error: any) {
      console.error('Error saving daily login type:', error);
      setNotification({
        open: true,
        message:
          error?.response?.data?.message || t('dailyLoginTypes.error.save'),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedType) return;

    try {
      setSaving(true);
      await dailyLoginService.deleteType(branchId, selectedType.id);
      setNotification({
        open: true,
        message: t('dailyLoginTypes.success.deleted'),
        severity: 'success'
      });
      setDeleteDialogOpen(false);
      setSelectedType(null);
      fetchDailyLoginTypes();
    } catch (error: any) {
      console.error('Error deleting daily login type:', error);
      setNotification({
        open: true,
        message:
          error?.response?.data?.message || t('dailyLoginTypes.error.delete'),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <LocalOfferIcon color="primary" />
          <Typography variant="h5">{t('dailyLoginTypes.title')}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
        >
          {t('dailyLoginTypes.add')}
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {dailyLoginTypes.length === 0 ? (
        <Alert severity="info">{t('dailyLoginTypes.empty')}</Alert>
      ) : (
        <Grid container spacing={2}>
          {dailyLoginTypes.map((type) => (
            <Grid item xs={12} md={6} lg={4} key={type.id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  opacity: type.isActive ? 1 : 0.6,
                  borderColor: type.isActive ? 'primary.main' : 'grey.400'
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="h6" component="div">
                          {type.name}
                        </Typography>
                        {!type.isActive && (
                          <Chip
                            label={t('inactive')}
                            size="small"
                            color="default"
                          />
                        )}
                      </Box>

                      {type.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mb={1}
                        >
                          {type.description}
                        </Typography>
                      )}

                      <Typography
                        variant="h5"
                        color="primary"
                        fontWeight="bold"
                        mb={1}
                      >
                        {type.price} TL
                      </Typography>

                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CardGiftcardIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {type.voucherTemplate?.name || t('unknown')}
                        </Typography>
                      </Box>
                    </Box>

                    <Box>
                      <Tooltip title={t('edit')}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(type)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(type)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedType
            ? t('dailyLoginTypes.edit')
            : t('dailyLoginTypes.create')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Alert severity="info">
              {t('dailyLoginTypes.voucherUsageInfo')}
            </Alert>

            <TextField
              fullWidth
              label={t('name')}
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
            />

            <TextField
              fullWidth
              label={t('description')}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={2}
            />

            <NumericInput
              fullWidth
              label={t('price')}
              value={formData.price}
              onChange={(value) =>
                setFormData({ ...formData, price: value || 0 })
              }
              allowDecimals={true}
              allowNegative={false}
              min={0}
              max={100000}
              decimalPlaces={2}
              InputProps={{
                endAdornment: <InputAdornment position="end">TL</InputAdornment>
              }}
            />

            <Autocomplete
              fullWidth
              options={voucherTemplates}
              value={selectedVoucherTemplate}
              onChange={(_, newValue) => {
                setSelectedVoucherTemplate(newValue);
                setFormData({
                  ...formData,
                  voucherTemplateId: newValue?.id || 0
                });
              }}
              getOptionLabel={(option) => option.name}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description || t('noDescription')}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('dailyLoginTypes.voucherTemplate')}
                  required
                  helperText={t('dailyLoginTypes.voucherTemplateHelp')}
                />
              )}
              noOptionsText={t('dailyLoginTypes.noVoucherTemplates')}
            />

            <Box sx={{ mt: -2 }}>
              <Button
                size="small"
                variant="text"
                startIcon={<AddIcon />}
                onClick={() => setVoucherDialogOpen(true)}
              >
                {t('dailyLoginTypes.createVoucherTemplate')}
              </Button>
            </Box>

            {/* Fiscal fiş alanları — legal KDV + opsiyonel satır dekompozisyonu.
                Boş bırakılabilir; boşsa cihaz branch defaultTaxGroupCode'una
                düşer (ops warn logunu görüp fark eder). */}
            <FormControl fullWidth>
              <InputLabel id="daily-login-vatRate-label">
                {t('dailyLoginTypes.entranceVatRate')}
              </InputLabel>
              <Select
                labelId="daily-login-vatRate-label"
                label={t('dailyLoginTypes.entranceVatRate')}
                value={formData.vatRate ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    vatRate:
                      e.target.value === ''
                        ? null
                        : (Number(e.target.value) as 0 | 1 | 10 | 20)
                  })
                }
              >
                <MenuItem value="">
                  <em>{t('dailyLoginTypes.entranceVatRate.unspecified')}</em>
                </MenuItem>
                {LEGAL_VAT_RATES.map((r) => (
                  <MenuItem key={r} value={r}>
                    %{r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Accordion
              defaultExpanded={
                formData.productPortionLabel != null ||
                formData.productPortionAmount != null ||
                formData.productPortionVatRate != null
              }
              sx={{ boxShadow: 'none', border: 1, borderColor: 'divider' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  {t('dailyLoginTypes.decomposition.title')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('dailyLoginTypes.decomposition.help')}
                </Alert>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label={t('dailyLoginTypes.decomposition.productLabel')}
                    value={formData.productPortionLabel ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        productPortionLabel:
                          e.target.value === '' ? null : e.target.value
                      })
                    }
                  />
                  <NumericInput
                    fullWidth
                    label={t('dailyLoginTypes.decomposition.productAmount')}
                    value={formData.productPortionAmount ?? 0}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        productPortionAmount:
                          value == null || value === 0 ? null : value
                      })
                    }
                    allowDecimals={true}
                    allowNegative={false}
                    min={0}
                    max={100000}
                    decimalPlaces={2}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">TL</InputAdornment>
                      )
                    }}
                  />
                  <FormControl fullWidth>
                    <InputLabel id="daily-login-productPortionVatRate-label">
                      {t('dailyLoginTypes.decomposition.productVatRate')}
                    </InputLabel>
                    <Select
                      labelId="daily-login-productPortionVatRate-label"
                      label={t('dailyLoginTypes.decomposition.productVatRate')}
                      value={formData.productPortionVatRate ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          productPortionVatRate:
                            e.target.value === ''
                              ? null
                              : (Number(e.target.value) as 0 | 1 | 10 | 20)
                        })
                      }
                    >
                      <MenuItem value="">
                        <em>
                          {t(
                            'dailyLoginTypes.decomposition.vatRateUnspecified'
                          )}
                        </em>
                      </MenuItem>
                      {LEGAL_VAT_RATES.map((r) => (
                        <MenuItem key={r} value={r}>
                          %{r}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </AccordionDetails>
            </Accordion>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
              }
              label={t('active')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kupon şablonu oluşturma — giriş tipi Dialog'u altta AÇIK kalır (MUI iç
          içe Dialog'u destekler), böylece yarım dolmuş form korunur. */}
      <VoucherTemplateDialog
        open={voucherDialogOpen}
        onClose={() => setVoucherDialogOpen(false)}
        editingTemplate={null}
        lockedBranchId={branchId}
        lockedCompanyId={companyId}
        onCreateTemplate={handleCreateVoucherTemplate}
        onUpdateTemplate={async () => {
          // Bu akış yalnız oluşturur; düzenleme /management/vouchers'ta yapılır.
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('dailyLoginTypes.deleteConfirm.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('dailyLoginTypes.deleteConfirm.message', {
              name: selectedType?.name
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={notification.severity}
          onClose={() => setNotification({ ...notification, open: false })}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DailyLoginTypesManager;
